import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { KeyRound, Plus, Ban, Copy, ShieldAlert, Terminal, ListChecks } from "lucide-react";
import { KeyUsageDialog } from "../components/KeyUsageDialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";

const BASE = process.env.REACT_APP_BACKEND_URL;
const fmt = (d) => (d ? new Date(d).toLocaleString() : "Never");

function KeyRow({ k, onRevoke, onUsage }) {
  const revoked = !!k.revoked_at;
  return (
    <div className="flex flex-wrap items-center gap-3 py-3" data-testid={`apikey-row-${k.id}`}>
      <span className={`flex h-9 w-9 items-center justify-center rounded-lg ${revoked ? "bg-muted text-muted-foreground" : "bg-primary/15 text-primary"}`}><KeyRound className="h-4 w-4" /></span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="font-medium" data-testid={`apikey-name-${k.id}`}>{k.name}</span>
          <span className={`rounded px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wider ${revoked ? "bg-destructive/15 text-destructive" : "bg-primary/15 text-primary"}`} data-testid={`apikey-status-${k.id}`}>{revoked ? "Revoked" : "Active"}</span>
        </div>
        <div className="font-mono text-xs text-muted-foreground">{k.prefix}</div>
        <div className="text-[11px] text-muted-foreground">Created {fmt(k.created_at)} · Last used {fmt(k.last_used_at)}</div>
      </div>
      <Button size="sm" variant="ghost" onClick={() => onUsage(k)} data-testid={`apikey-usage-${k.id}`}>
        <ListChecks className="mr-1.5 h-3.5 w-3.5" /> Activity
      </Button>
      {!revoked && (
        <Button size="sm" variant="outline" onClick={() => onRevoke(k)} data-testid={`apikey-revoke-${k.id}`}>
          <Ban className="mr-1.5 h-3.5 w-3.5" /> Revoke
        </Button>
      )}
    </div>
  );
}

function Docs() {
  return (
    <div className="rounded-xl border border-border bg-card p-6" data-testid="apikey-docs">
      <div className="mb-3 flex items-center gap-2 text-sm font-medium"><Terminal className="h-4 w-4 text-primary" /> Calling the vault from your server</div>
      <pre className="overflow-x-auto rounded-md bg-muted/60 p-4 font-mono text-xs leading-relaxed">{`# Tokenize a card
curl -X POST ${BASE}/api/v1/tokenize \\
  -H "X-API-Key: $CITIDAN_API_KEY" -H "Content-Type: application/json" \\
  -d '{"card_number":"4111111111111111","expiry":"12/29","holder_name":"Jane Doe","cvv":"123"}'

# Charge a stored token (live: true uses your saved processor)
curl -X POST ${BASE}/api/v1/charges \\
  -H "X-API-Key: $CITIDAN_API_KEY" -H "Content-Type: application/json" \\
  -d '{"token":"tok_...","amount":2500,"currency":"USD","email":"customer@example.com","live":true}'   # omit live for a test (simulated) charge`}</pre>
      <p className="mt-3 text-xs text-muted-foreground">Keep keys on your server only. Never put them in browser or mobile app code.</p>
    </div>
  );
}

export default function ApiKeys() {
  const [keys, setKeys] = useState([]);
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const [fresh, setFresh] = useState(null);
  const [usageKey, setUsageKey] = useState(null);

  const load = () => api.get("/api-keys").then((r) => setKeys(r.data.keys)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const r = await api.post("/api-keys", { name });
      setFresh(r.data.key);
      setName("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (k) => {
    if (!window.confirm(`Revoke "${k.name}"? Systems using this key will stop working immediately.`)) return;
    try {
      await api.post(`/api-keys/${k.id}/revoke`);
      toast.success(`Key "${k.name}" revoked`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const copyFresh = () => { navigator.clipboard.writeText(fresh); toast.success("API key copied"); };

  return (
    <div className="space-y-8">
      <div>
        <div className="text-xs font-mono uppercase tracking-wider text-primary">Programmatic Access</div>
        <h1 className="mt-1 text-3xl font-bold tracking-tight sm:text-4xl">API Keys</h1>
        <p className="mt-2 text-sm text-muted-foreground">Let your own systems tokenize and charge cards without logging in. Each key can be revoked on its own.</p>
      </div>

      <form onSubmit={create} className="flex flex-wrap items-end gap-3 rounded-xl border border-border bg-card p-6" data-testid="apikey-form">
        <div className="min-w-[240px] flex-1 space-y-1.5">
          <Label>Key name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} required placeholder="e.g. Production checkout server" data-testid="apikey-name-input" />
        </div>
        <Button type="submit" disabled={creating || !name.trim()} data-testid="apikey-create-btn">
          <Plus className="mr-1.5 h-4 w-4" /> {creating ? "Creating…" : "Create key"}
        </Button>
      </form>

      <div className="rounded-xl border border-border bg-card p-6" data-testid="apikey-list">
        <h3 className="mb-2 text-lg font-medium">Your keys ({keys.length})</h3>
        {keys.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">No API keys yet.</p>
        ) : (
          <div className="divide-y divide-border">{keys.map((k) => <KeyRow key={k.id} k={k} onRevoke={revoke} onUsage={setUsageKey} />)}</div>
        )}
      </div>

      <Docs />

      <KeyUsageDialog apiKey={usageKey} onClose={() => setUsageKey(null)} />

      <Dialog open={!!fresh} onOpenChange={(o) => !o && setFresh(null)}>
        <DialogContent data-testid="apikey-created-dialog">
          <DialogHeader>
            <DialogTitle>Copy your new API key</DialogTitle>
            <DialogDescription>This is the only time the full key is shown. If you lose it, revoke it and create a new one.</DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 rounded-md border border-border bg-muted/40 p-3">
            <code className="min-w-0 flex-1 break-all font-mono text-xs" data-testid="apikey-created-value">{fresh}</code>
            <Button size="icon" variant="ghost" onClick={copyFresh} data-testid="apikey-copy-btn"><Copy className="h-4 w-4" /></Button>
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground"><ShieldAlert className="h-4 w-4 text-primary" /> Only a fingerprint of this key is stored in the vault.</div>
          <DialogFooter><Button onClick={() => setFresh(null)} data-testid="apikey-done-btn">I've saved it</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
